import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  CreditCard, Crown, AlertTriangle,
  Loader2, Mail, Building2, FileText, Sparkles, ExternalLink, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentHistory from '../../components/billing/PaymentHistory';
import PlanSelectionDialog from '../../components/billing/PlanSelectionDialog';
import SubscriptionActionDialog from '../../components/billing/SubscriptionActionDialog';
import QaModePanel from '../../components/billing/QaModePanel';

export default function BillingPage() {
  const { api, refreshUser } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const [billing, setBilling] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardActionLoading, setCardActionLoading] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [cancelSchedDialogOpen, setCancelSchedDialogOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post('/payments/customer-portal');
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener');
        return;
      }
      toast.error('Could not open the Stripe portal.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open the Stripe portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [billingRes, txnRes] = await Promise.all([
        api.get('/payments/billing'),
        api.get('/payments/transactions'),
      ]);
      setBilling(billingRes.data);
      setTransactions(txnRes.data);
    } catch {
      toast.error('Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Handle ?setup_session_id= (return from Stripe setup checkout)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('setup_session_id');
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/payments/setup-card-status/${sid}`);
        if (cancelled) return;
        if (res.data?.saved) {
          toast.success('Card saved successfully — you can now post shifts.');
          if (refreshUser) refreshUser();
          fetchAll();
        } else {
          toast.message('Card setup not completed.');
        }
      } catch {
        toast.error('Could not verify card setup.');
      } finally {
        // Clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete('setup_session_id');
        window.history.replaceState({}, document.title, url.pathname + url.search);
      }
    })();
    return () => { cancelled = true; };
  }, [api, refreshUser, fetchAll]);

  const handleAddOrUpdateCard = async () => {
    setCardActionLoading(true);
    try {
      const res = await api.post('/payments/setup-card', { originUrl: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Could not start card setup');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Could not start card setup');
    } finally {
      setCardActionLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto" data-testid="billing-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-mgr-billing' : undefined}>Billing & Subscription</h1>
        <p className="text-slate-500 mt-1">Manage your plan, payments, and billing details.</p>
      </div>

      {/* Payment lock banner */}
      {billing?.paymentLocked && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="billing-payment-locked-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Account locked — payment failed</p>
            <p className="text-xs text-red-700 mt-0.5">
              {billing.paymentLockedReason || 'A previous shift charge failed. Update your card to resume posting.'}
            </p>
          </div>
          <Button
            size="sm"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white h-11 shrink-0"
            onClick={handleAddOrUpdateCard}
            disabled={cardActionLoading}
            data-testid="locked-update-card-btn"
          >
            {cardActionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Update Card'}
          </Button>
        </div>
      )}

      {/* Cancel-at-period-end banner (iter90) */}
      {billing?.cancelAtPeriodEnd && !billing?.pendingPlan && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="cancel-scheduled-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Cancellation scheduled</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Your plan will end on{' '}
              <strong>
                {billing.cancellationEffectiveAt
                  ? new Date(billing.cancellationEffectiveAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : 'the end of the current period'}
              </strong>. You can resume any time before that date.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setResumeDialogOpen(true)}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 shrink-0"
            data-testid="resume-subscription-btn"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Resume subscription
          </Button>
        </div>
      )}

      {/* Pending plan-change banner */}
      {billing?.pendingPlan && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3" data-testid="pending-transition-banner">
          <RefreshCw className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900">Scheduled plan change</p>
            <p className="text-xs text-blue-800 mt-0.5">
              You&apos;ll switch to <strong className="capitalize">{billing.pendingPlan}</strong> on{' '}
              <strong>
                {billing.pendingPlanEffectiveAt
                  ? new Date(billing.pendingPlanEffectiveAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : 'the end of the current period'}
              </strong>. Your current plan stays active until then.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCancelSchedDialogOpen(true)}
            className="rounded-full h-11 px-4 shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
            data-testid="cancel-scheduled-change-btn"
          >
            Cancel switch
          </Button>
        </div>
      )}


      {/* ===================== ACTIVE PLAN & TRIAL ===================== */}
      <QaModePanel />
      <div data-testid="active-plan-section">
        <Card className="relative overflow-hidden border border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-7">
            <div className="flex flex-col sm:flex-row sm:items-start gap-5">
              <div className="h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                <Crown className="h-6 w-6 text-[#4338CA]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-2xl font-bold text-slate-900" data-testid="active-plan-heading">
                    {billing?.planName || 'Your ShiftCover365 plan'}
                  </h3>
                  {billing?.foundingMember && (
                    <Badge className="bg-amber-100 text-amber-800 font-semibold text-xs flex items-center gap-1" data-testid="plan-founding-badge">
                      <Sparkles className="h-3 w-3" />
                      Founding Member
                    </Badge>
                  )}
                  {billing?.subscriptionStatus === 'trialing' && (
                    <Badge className="bg-emerald-100 text-emerald-700 font-semibold text-xs" data-testid="plan-trialing-badge">Free trial</Badge>
                  )}
                  {billing?.subscriptionStatus === 'active' && (
                    <Badge className="bg-emerald-100 text-emerald-700 font-semibold text-xs" data-testid="plan-active-badge">Active</Badge>
                  )}
                  {billing?.subscriptionStatus === 'canceled' && (
                    <Badge className="bg-slate-200 text-slate-700 font-semibold text-xs" data-testid="plan-canceled-badge">Cancelled</Badge>
                  )}
                </div>
                {(billing?.effectivePriceDisplay || billing?.planDisplayPrice) && (
                  <div className="flex items-baseline gap-2 mt-3">
                    <span className="text-3xl font-extrabold text-slate-900 tabular-nums" data-testid="plan-display-price">
                      {billing?.effectivePriceDisplay || billing.planDisplayPrice}
                    </span>
                    <span className="text-sm font-medium text-slate-500">
                      {billing?.planType === 'payg'
                        ? '/shift, billed monthly'
                        : billing?.planType === 'annual'
                          ? '/year'
                          : '/month'}
                    </span>
                  </div>
                )}
                {/* Plan summary grid — iter89 */}
                {billing?.planType && billing.planType !== 'none' && (
                  <dl
                    className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-t border-slate-100 pt-4"
                    data-testid="plan-summary-grid"
                  >
                    <div>
                      <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</dt>
                      <dd className="mt-0.5 font-semibold text-slate-900 capitalize" data-testid="plan-summary-status">
                        {billing.subscriptionStatus || '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Trial ends</dt>
                      <dd className="mt-0.5 font-semibold text-slate-900" data-testid="plan-summary-trial-end">
                        {billing.trialEnd
                          ? new Date(billing.trialEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Next payment</dt>
                      <dd className="mt-0.5 font-semibold text-slate-900" data-testid="plan-summary-next-payment">
                        {billing.nextBillingDate
                          ? new Date(billing.nextBillingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : billing.planType === 'payg'
                            ? 'End of month'
                            : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Coupon</dt>
                      <dd className="mt-0.5 font-semibold text-slate-900 truncate" data-testid="plan-summary-coupon">
                        {billing.couponId ? '£10 off forever' : '—'}
                      </dd>
                    </div>
                  </dl>
                )}
                <p className="text-[14px] text-slate-700 mt-4 leading-relaxed" data-testid="plan-description">
                  Every new manager account gets a <strong>14-day free trial</strong>. Add a payment method any time before day 14 to keep using paid plans — or roll into Pay-As-You-Go (<strong>£3.99 per successfully completed shift, billed monthly</strong>) automatically. The first 20 Monthly customers lock in a <strong>Founding Member</strong> rate of £19.99/month for as long as their subscription stays active.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    onClick={handleOpenPortal}
                    disabled={portalLoading || !billing?.stripeCustomerId}
                    className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white h-11 px-6"
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    )}
                    Manage subscription
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPlanDialogOpen(true)}
                    className="rounded-full h-11 px-6 border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="change-plan-btn"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Change plan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.location.assign('/manager/billing/usage')}
                    className="rounded-full h-11 px-6 border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="view-usage-statement-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View usage statement
                  </Button>
                  {billing?.stripeSubscriptionId && !billing?.cancelAtPeriodEnd && (
                    <Button
                      variant="ghost"
                      onClick={() => setCancelDialogOpen(true)}
                      className="rounded-full h-11 px-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="cancel-subscription-btn"
                    >
                      Cancel subscription
                    </Button>
                  )}
                  <a
                    href="mailto:support@shiftcover365.co.uk?subject=Billing%20question%20%E2%80%94%20ShiftCover365"
                    data-testid="contact-support-btn"
                    className="inline-flex"
                  >
                    <Button
                      variant="ghost"
                      className="rounded-full h-11 px-6 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Contact support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===================== SECTION 3: PAYMENT METHOD ===================== */}
      <Card className="border-0 shadow-sm" data-testid="payment-method-section">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-slate-400" />
              Payment Method
            </h2>
            <Button
              variant="outline"
              className="rounded-full h-11"
              onClick={handleAddOrUpdateCard}
              disabled={cardActionLoading}
              data-testid={billing?.paymentMethodLast4 ? 'update-card-btn' : 'add-card-btn'}
            >
              {cardActionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : billing?.paymentMethodLast4 ? (
                'Update Card'
              ) : (
                'Add Card'
              )}
            </Button>
          </div>

          {billing?.paymentMethodLast4 ? (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="h-10 w-14 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900" data-testid="payment-method-summary">
                    {billing.paymentMethodBrand && <span className="capitalize">{billing.paymentMethodBrand} </span>}
                    ending in {billing.paymentMethodLast4}
                  </p>
                  {billing.paymentMethodExpiry && (
                    <p className="text-xs text-slate-500">Expires {billing.paymentMethodExpiry}</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-50 rounded-xl text-center" data-testid="no-payment-method-state">
              <CreditCard className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No payment method on file.</p>
              <p className="text-xs text-slate-400 mt-1">Add a card so we can take payment for completed shifts automatically.</p>
            </div>
          )}

          {/* Billing details */}
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Billing Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Mail className="h-4 w-4 text-slate-400" />
                <span className="text-slate-500">Email:</span>
                <span className="font-medium" data-testid="billing-email">{billing?.billingEmail || '-'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Building2 className="h-4 w-4 text-slate-400" />
                <span className="text-slate-500">Name:</span>
                <span className="font-medium" data-testid="billing-name">{billing?.businessName || '-'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===================== SECTION 4: PAYMENT HISTORY ===================== */}
      <PaymentHistory transactions={transactions} />

      {/* Plan selection modal */}
      <PlanSelectionDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        currentPlanType={billing?.planType}
        isFoundingMember={Boolean(billing?.foundingMember)}
        hasActiveSub={
          Boolean(billing?.stripeSubscriptionId) &&
          ['active', 'trialing', 'past_due', 'unpaid'].includes(billing?.subscriptionStatus) &&
          billing?.planType !== 'payg' &&
          billing?.planType !== 'none'
        }
        onChanged={fetchAll}
      />

      {/* Cancel subscription */}
      <SubscriptionActionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel subscription?"
        description="Your access will continue until the end of the current billing period. Nothing else changes today."
        effectiveMessage={
          billing?.subscriptionEnd
            ? `Access will end on ${new Date(billing.subscriptionEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}. You can resume any time before that date.`
            : 'Access will end at the end of the current billing period.'
        }
        destructive
        primaryLabel="Cancel subscription"
        primaryTestid="confirm-cancel-btn"
        endpoint="/payments/cancel-subscription"
        onSuccess={fetchAll}
      />

      {/* Resume subscription */}
      <SubscriptionActionDialog
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        title="Resume subscription?"
        description="Undo the scheduled cancellation. Your plan will continue as normal at the next billing cycle."
        primaryLabel="Resume subscription"
        primaryTestid="confirm-resume-btn"
        endpoint="/payments/resume-subscription"
        onSuccess={fetchAll}
      />

      {/* Cancel scheduled plan change */}
      <SubscriptionActionDialog
        open={cancelSchedDialogOpen}
        onOpenChange={setCancelSchedDialogOpen}
        title="Cancel scheduled plan change?"
        description="Your current plan will keep running as normal. The scheduled switch will not happen."
        primaryLabel="Cancel scheduled switch"
        primaryTestid="confirm-cancel-scheduled-btn"
        endpoint="/payments/cancel-scheduled-change"
        onSuccess={fetchAll}
      />
    </div>
  );
}
